'use strict';

const helper = require('../../helper');
const venmoBillingAddressHelper = require('./billingAddress');
const paymentMethodGeneralHelper = require('../../paymentMethodGeneralHelper');

const $addVenmoAccountBtn = document.querySelector('.js-add-venmo-account');
const $venmoAccountWrapper = document.querySelector('.js-braintree-account-venmo-button-wrapper');
const newVenmoOptionEl = document.getElementById('newVenmoAccount');
const venmoTabEl = document.querySelector('.nav-item[data-method-id="Venmo"]');

const btVenmoButtonClassName = '.js-braintree-venmo-button';

const CLASS_D_NONE = 'd-none';

/**
 * Sets the enriched customer data value to the data attribute
 * @param {boolean} isEnrichedCustomerDataEnabled Whether the enriched customer data is enabled on the merchant side
 */
const setEnrichedCustomerDataValue = isEnrichedCustomerDataEnabled => {
    newVenmoOptionEl?.setAttribute('data-is-enriched-customer-data-enabled', isEnrichedCustomerDataEnabled);
};

/**
 * Returns Braintree Venmo configs
 * @returns {Object} Braintree Venmo configs
 */
function getBraintreeVenmoButtonConfigs() {
    const $venmoButton = document.querySelector('.js-braintree-venmo-button, .js-braintree-minicart-venmo-button');

    return helper.tryParseJSON($venmoButton.getAttribute('data-braintree-config'));
}

/**
 * Shows Venmo button on the Account Page and hides "add new" button
 */
function showVenmoAccountBtn() {
    $addVenmoAccountBtn.classList.add(CLASS_D_NONE);
    $venmoAccountWrapper.classList.remove(CLASS_D_NONE);
}

/**
 * Hides Venmo button on the Account Page and shows "add new" button
 */
function hideVenmoAccountBtn() {
    $addVenmoAccountBtn.classList.remove(CLASS_D_NONE);
    $venmoAccountWrapper.classList.add(CLASS_D_NONE);
}

/**
 * Saves Venmo device (risk) data to hidden input
 * @param {string} deviceData Venmo device (risk) data
 */
function setVenmoDeviceData(deviceData) {
    const venmoDeviceDataEl = document.getElementById('braintree-venmo-device-data');

    if (venmoDeviceDataEl) {
        venmoDeviceDataEl.value = deviceData;
    }
}

/**
 * Checks billing form on the Billing Page
 * If billing form is valid, saves/updates billing address to input
 * If not valid (possible case: empty email) - add error class to email input
 * @returns {boolean} billing form check result value
 */
function checkBillingForm() {
    const $billingForm = document.querySelector('.card-body [id=dwfrm_billing]');
    const $venmoBillingInput = document.getElementById('braintreeVenmoBilling');

    if ($billingForm.checkValidity()) {
        /**
            * Setting current customer Billing address to form
            * For the case when SFCC redirects customer from payment stage to shipping stage
            * after successful Venmo app response
        */
        $venmoBillingInput.value = JSON.stringify(helper.getBillingAddressFormValues());

        return true;
    }

    return false;
}

/**
 * Checks whether amount is zero
 * If amount is zero Venmo pop-up is closed and appropriate error is shown
 * If amount is acceptable by Venmo user could proceed through pop-up
 * @param {number} amount Basket total price
 * @param {Object} messages Messages
 * @returns {boolean} zero amount check result value
 */
function checkZeroAmount(amount, messages) {
    const VenmoAlertHandlerModel = require('../alertHandler/venmoAlertHandlerModel');
    const alertHandler = new VenmoAlertHandlerModel();

    alertHandler.hideAlerts();

    if (amount === 0) {
        alertHandler.showError(messages.VENMO_ZERO_AMOUNT_ERROR);

        return true;
    }

    return false;
}

/**
 * Handles all behavior related to the Venmo content tab
 */
function venmoContentTabHandler() {
    const paymentMethodGeneral = require('../../paymentMethodGeneral');

    const btVenmoAccountsListEl = document.getElementById('braintreeVenmoAccountsList');
    const saveVenmoAccountCheckboxContainerEl = document.getElementById('braintreeSaveVenmoAccountContainer');
    const saveVenmoAccountCheckboxEl = document.getElementById('braintreeSaveVenmoAccount');
    const venmoButtonEl = document.querySelector(btVenmoButtonClassName);
    const venmoContentEl = document.querySelector('.js-braintree-venmo-content');

    const venmoOptionId = btVenmoAccountsListEl.value;

    paymentMethodGeneral.removeAddedOption();
    paymentMethodGeneral.selectBillingAddress(venmoTabEl);

    if (venmoOptionId === 'newaccount') {
        const showSaveBillingAddressCheckbox = false;

        paymentMethodGeneralHelper.disableBillingAddressFunctionality();
        venmoBillingAddressHelper.handleSaveBillingAddressCheckbox(showSaveBillingAddressCheckbox);
        // Case when option "new account" is selected in dropdown
        venmoButtonEl.classList.remove(CLASS_D_NONE);

        helper.continueButtonToggle(true);
        venmoContentEl.setAttribute('data-paypal-is-hide-continue-button', true);
        // Case when registered user and vaulting is enabled
        if (saveVenmoAccountCheckboxContainerEl) {
            saveVenmoAccountCheckboxContainerEl.classList.remove(CLASS_D_NONE);

            helper.updateCheckboxValueOnChange(saveVenmoAccountCheckboxEl);
        }
    } else {
        venmoBillingAddressHelper.handleSavedBillingAddressFunctionality();
        // Case when saved account is selected in dropdown
        venmoButtonEl.classList.add(CLASS_D_NONE);

        helper.continueButtonToggle(false);
        venmoContentEl.setAttribute('data-paypal-is-hide-continue-button', false);
        // Case when registered user and vaulting is enabled
        if (saveVenmoAccountCheckboxContainerEl) {
            saveVenmoAccountCheckboxContainerEl.classList.add(CLASS_D_NONE);
        }
    }
}

/**
 * Formats Venmo address with SFRA address object props
 * @param {Object} address Address from Venmo payload
 * @returns {Object | undefined} Formatted address or undefined
 */
function formatAddress(address) {
    if (!address) {
        return undefined;
    }

    const fullName = address.fullName && address.fullName.split(/\s+/);

    if (address.countryCode === 'USA') {
        address.countryCode = 'US';
    }

    return {
        lastName: address.lastName || (fullName && fullName.pop()),
        firstName: address.firstName || (fullName && fullName.join(' ')),
        address1: decodeURIComponent(address.addressLine1),
        address2: address.addressLine2 ? decodeURIComponent(address.addressLine2) : '',
        city: decodeURIComponent(address.adminArea2),
        stateCode: address.adminArea1,
        postalCode: decodeURIComponent(address.postalCode),
        countryCode: decodeURIComponent(address.countryCode)
    };
}

/**
 * Creates a billing address based on Venmo payer info
 * @param {Object} payerInfo Payer info from Venmo
 * @returns {Object | undefined} Billing address or undefined
 */
function createBillingAddress(payerInfo) {
    const billingAddress = formatAddress(payerInfo.billingAddress);

    if (!billingAddress) {
        return undefined;
    }

    if (!billingAddress.firstName) {
        billingAddress.firstName = payerInfo.firstName;
    }

    if (!billingAddress.lastName) {
        billingAddress.lastName = payerInfo.lastName;
    }

    billingAddress.phone = payerInfo.phoneNumber;

    return billingAddress;
}

/**
 * Creates a shipping address based on Venmo payer info
 * @param {Object} payerInfo Payer info from Venmo
 * @returns {Object | undefined} Shipping address or undefined
 */
function createShippingAddress(payerInfo) {
    const shippingAddress = formatAddress(payerInfo.shippingAddress);

    if (!shippingAddress) {
        return undefined;
    }

    return {
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        countryCodeAlpha2: shippingAddress.countryCode,
        locality: shippingAddress.city,
        streetAddress: shippingAddress.address1,
        extendedAddress: shippingAddress.address2,
        postalCode: shippingAddress.postalCode,
        region: shippingAddress.stateCode,
        phone: payerInfo.phoneNumber
    };
}

/**
 * Removes Venmo block from the DOM
 */
function removeVenmoBlock() {
    const venmoAccountCardEl = document.querySelector('.js-venmo-card');
    const venmoBillingCardEl = document.querySelector('.js-braintree-venmo-content');
    const venmoExpressCheckoutCardEl = document.querySelector('.js-venmo-express-checkout');

    if (venmoAccountCardEl) {
        venmoAccountCardEl.remove();
    } else if (venmoBillingCardEl) {
        venmoTabEl?.remove();
        venmoAccountCardEl?.remove();
    } else if (venmoExpressCheckoutCardEl) {
        venmoExpressCheckoutCardEl.remove();
    }
}

module.exports = {
    getBraintreeVenmoButtonConfigs,
    showVenmoAccountBtn,
    hideVenmoAccountBtn,
    setVenmoDeviceData,
    checkBillingForm,
    checkZeroAmount,
    venmoContentTabHandler,
    createBillingAddress,
    setEnrichedCustomerDataValue,
    createShippingAddress,
    removeVenmoBlock
};
